<template>
<div>
  <!--    Breadcrumbs-->
  <breadcrumb style="margin-left: -15px">
    <breadcrumb-item>
      <a :href="fromClues ? '/games/clues/select' : '/games/settings'">{{ fromClues ? 'Interaction settings' : 'Event settings' }}</a>
    </breadcrumb-item>
    <breadcrumb-item>
      <a :href="fromClues ? '/games/clues/configure?id=' + game.id : '/games/edit?id=' + game.id">{{ game.name }}</a>
    </breadcrumb-item>
    <breadcrumb-item>
      <span>Communication screen settings</span>
    </breadcrumb-item>
  </breadcrumb>
  <!--    Loader-->
  <loading-panel :loading="loading"></loading-panel>
  <div class="row mt-3">
    <div class="col-5">
      <card >
        <div class="row mb-3">
          <div class="col">
            <tab-rail
              :tabs="categories"
              :value="activeTabIndex"
              @input="switchTab"
            />
          </div>
        </div>

        <div v-show="activeTabIndex === 0">

          <!-- Timer -->
          <div class="cdc-section">
            <h4 class="cdc-section__title">Timer</h4>
            <div class="row">
              <div class="col-6">
                <el-switch active-color="#e14eca" v-model="gameConfig.has_timer" @change="toggleTimerLayout()" />
                <label> &nbsp; Include timer</label>
              </div>
              <div v-if="gameConfig.has_timer" class="col-5">
                <base-input label="Time limit (hh:mm:ss)" placeholder="00:45:00" v-mask="'##:##:##'" v-model="gameConfig.max_time"></base-input>
              </div>
            </div>
          </div>

          <!-- Interactions -->
          <div class="cdc-section">
            <h4 class="cdc-section__title">Interactions</h4>
            <div class="row">
              <div class="col-6">
                <el-switch active-color="#e14eca" v-model="gameConfig.has_clue_limit" @change="toggleHintsLayout()" />
                <label> &nbsp; Limit the number of interactions</label>
              </div>
              <div v-if="gameConfig.has_clue_limit" class="col-5">
                <base-input label="Maximum number of interactions" type="number" v-model="gameConfig.max_clues"></base-input>
              </div>
            </div>
          </div>

          <!-- Sound -->
          <div class="cdc-section">
            <h4 class="cdc-section__title">Pre-interaction sound</h4>
            <div>
              <el-switch active-color="#e14eca" v-model="gameConfig.play_sound_before_hints" />
              <label> &nbsp; Play a sound before an interaction is displayed</label>
            </div>
            <div v-if="gameConfig.play_sound_before_hints" class="cdc-sub-content mt-2">
              <div v-if="gameConfig.pre_hint_sound_file" class="cdc-file-card">
                <div class="cdc-file-card__icon">
                  <i class="tim-icons icon-volume-98" style="font-size: 18px; color: #e14eca;"></i>
                </div>
                <div class="cdc-file-card__info">
                  <audio controls class="cdc-audio-player" :key="gameConfig.pre_hint_sound_file">
                    <source :src="gameConfig.pre_hint_sound_file">
                  </audio>
                </div>
                <div class="cdc-file-card__actions">
                  <span class="cdc-replace-btn" @click="$refs.preHintSoundInput.click()">Replace</span>
                </div>
              </div>
              <div v-else class="cdc-dropzone cdc-dropzone--compact" @click="$refs.preHintSoundInput.click()">
                <i class="tim-icons icon-cloud-upload-94"></i>
                <span>Drop audio file or click to browse</span>
              </div>
              <input ref="preHintSoundInput" type="file" accept="audio/*" style="display: none;" @change="handlePreviewSound" />
              <div class="mt-3">
                <label class="cdc-sub-label">Play sound before interaction type:</label>
                <div class="cdc-inline-checks">
                  <base-checkbox v-model="gameConfig.play_sound_before_text">Text</base-checkbox>
                  <base-checkbox v-model="gameConfig.play_sound_before_custom">Custom</base-checkbox>
                  <base-checkbox v-model="gameConfig.play_sound_before_img">Image</base-checkbox>
                  <base-checkbox v-model="gameConfig.play_sound_before_audio">Audio</base-checkbox>
                  <base-checkbox v-model="gameConfig.play_sound_before_video">Video</base-checkbox>
                </div>
              </div>
            </div>
          </div>

          <div class="cdc-section">
            <h4 class="cdc-section__title">Background music</h4>
            <div>
              <el-switch active-color="#e14eca" v-model="gameConfig.play_background_music" />
              <label> &nbsp; Play background music</label>
            </div>
            <div v-if="gameConfig.play_background_music">
              <div>
                <el-switch active-color="#e14eca" v-model="gameConfig.loop_background_music" />
                <label> &nbsp; Loop</label>
              </div>
              <div v-if="gameConfig.background_music_sound_file" class="cdc-file-card mt-2" :key="i">
                <div class="cdc-file-card__icon">
                  <i class="tim-icons icon-note-03" style="font-size: 18px; color: #e14eca;"></i>
                </div>
                <div class="cdc-file-card__info">
                  <audio controls class="cdc-audio-player" :key="gameConfig.background_music_sound_file">
                    <source :src="gameConfig.background_music_sound_file">
                  </audio>
                </div>
                <div class="cdc-file-card__actions">
                  <span class="cdc-replace-btn" @click="$refs.bgMusicInput.click()">Replace</span>
                </div>
              </div>
              <div v-else class="cdc-dropzone cdc-dropzone--compact mt-2" @click="$refs.bgMusicInput.click()">
                <i class="tim-icons icon-cloud-upload-94"></i>
                <span>Drop audio file or click to browse</span>
              </div>
              <input ref="bgMusicInput" type="file" accept="audio/*" style="display: none;" @change="handleBackgroundMusicFileUpload" />
            </div>
          </div>

          <!-- Event start media -->
          <div class="cdc-section">
            <h4 class="cdc-section__title">Event start</h4>
            <div>
              <el-switch active-color="#e14eca" v-model="gameConfig.play_intro_video" />
              <label> &nbsp; Play media on event start</label>
            </div>
            <div v-if="gameConfig.play_intro_video" class="cdc-sub-content mt-2">
              <!-- Video preview -->
              <div v-if="gameConfig.intro_video_url && introMediaType === 'video'" class="cdc-file-card">
                <div class="cdc-file-card__preview">
                  <video class="cdc-video-thumb" :key="gameConfig.intro_video_url">
                    <source :src="gameConfig.intro_video_url">
                  </video>
                </div>
                <div class="cdc-file-card__info">
                  <span class="cdc-file-card__name">{{ gameConfig.intro_media_filename || 'Event start video' }}</span>
                  <span class="cdc-file-card__meta">Click replace to change</span>
                </div>
                <div class="cdc-file-card__actions">
                  <span class="cdc-replace-btn" @click="$refs.introMediaInput.click()">Replace</span>
                </div>
              </div>
              <!-- Audio preview -->
              <div v-else-if="gameConfig.intro_video_url && introMediaType === 'audio'" class="cdc-file-card">
                <div class="cdc-file-card__icon">
                  <i class="tim-icons icon-note-03" style="font-size: 18px; color: #e14eca;"></i>
                </div>
                <div class="cdc-file-card__info">
                  <span class="cdc-file-card__name">{{ gameConfig.intro_media_filename || 'Event start audio' }}</span>
                  <audio controls class="cdc-audio-player" :key="gameConfig.intro_video_url">
                    <source :src="gameConfig.intro_video_url">
                  </audio>
                </div>
                <div class="cdc-file-card__actions">
                  <span class="cdc-replace-btn" @click="$refs.introMediaInput.click()">Replace</span>
                </div>
              </div>
              <!-- Upload dropzone -->
              <div v-else class="cdc-dropzone cdc-dropzone--compact" @click="$refs.introMediaInput.click()">
                <i class="tim-icons icon-cloud-upload-94"></i>
                <span>Drop audio or video file or click to browse</span>
              </div>
              <input ref="introMediaInput" type="file" accept="audio/*,video/*" style="display: none;" @change="handleIntroMediaUpload" />
              <div class="mt-2">
                <label class="cdc-sub-label">Timer behavior</label>
                <base-radio v-model="gameConfig.intro_video_timer_mode" name="immediate">Start timer immediately with media</base-radio>
                <base-radio v-model="gameConfig.intro_video_timer_mode" name="after_video">Start timer after media ends</base-radio>
              </div>
            </div>
          </div>

          <!-- Event completion media -->
          <div class="cdc-section">
            <h4 class="cdc-section__title">Event completion</h4>
            <div>
              <el-switch active-color="#e14eca" v-model="gameConfig.play_completion_media" />
              <label> &nbsp; Play media on event completion</label>
            </div>
            <div v-if="gameConfig.play_completion_media" class="cdc-sub-content mt-2">
              <!-- Video preview -->
              <div v-if="gameConfig.completion_media_url && completionMediaType === 'video'" class="cdc-file-card">
                <div class="cdc-file-card__preview">
                  <video class="cdc-video-thumb" :key="gameConfig.completion_media_url">
                    <source :src="gameConfig.completion_media_url">
                  </video>
                </div>
                <div class="cdc-file-card__info">
                  <span class="cdc-file-card__name">{{ gameConfig.completion_media_filename || 'Event completion video' }}</span>
                  <span class="cdc-file-card__meta">Click replace to change</span>
                </div>
                <div class="cdc-file-card__actions">
                  <span class="cdc-replace-btn" @click="$refs.completionMediaInput.click()">Replace</span>
                </div>
              </div>
              <!-- Audio preview -->
              <div v-else-if="gameConfig.completion_media_url && completionMediaType === 'audio'" class="cdc-file-card">
                <div class="cdc-file-card__icon">
                  <i class="tim-icons icon-note-03" style="font-size: 18px; color: #e14eca;"></i>
                </div>
                <div class="cdc-file-card__info">
                  <span class="cdc-file-card__name">{{ gameConfig.completion_media_filename || 'Event completion audio' }}</span>
                  <audio controls class="cdc-audio-player" :key="gameConfig.completion_media_url">
                    <source :src="gameConfig.completion_media_url">
                  </audio>
                </div>
                <div class="cdc-file-card__actions">
                  <span class="cdc-replace-btn" @click="$refs.completionMediaInput.click()">Replace</span>
                </div>
              </div>
              <!-- Upload dropzone -->
              <div v-else class="cdc-dropzone cdc-dropzone--compact" @click="$refs.completionMediaInput.click()">
                <i class="tim-icons icon-cloud-upload-94"></i>
                <span>Drop audio or video file or click to browse</span>
              </div>
              <input ref="completionMediaInput" type="file" accept="audio/*,video/*" style="display: none;" @change="handleCompletionMediaUpload" />
            </div>
          </div>

          <!-- Display options -->
          <div class="cdc-section">
            <h4 class="cdc-section__title">Display options</h4>
            <div>
              <el-switch active-color="#e14eca" v-model="gameConfig.is_image_fullscreen" />
              <label> &nbsp; Display image interactions in full screen mode</label>
            </div>
            <div>
              <el-switch active-color="#e14eca" v-model="gameConfig.is_video_fullscreen" />
              <label> &nbsp; Play video interactions in full screen mode</label>
            </div>
            <div>
              <el-switch active-color="#e14eca" v-model="gameConfig.clear_video_hints" />
              <label> &nbsp; Automatically clear video interactions after ending</label>
            </div>
            <div>
              <el-switch active-color="#e14eca" v-model="gameConfig.display_logo" @change="toggleLogoLayout()" />
              <label> &nbsp; Include company logo</label>
            </div>
            <div>
              <el-switch active-color="#e14eca" v-model="gameConfig.display_background_image" @change="setLayoutCss()" />
              <label> &nbsp; Include a background image
                <el-popover trigger="hover" placement="right">
                  <div>
                    <div class="popover-body">Recommended resolution sizes:
                      <br/> Aspect Ratio: <b>16:9</b>
                      <br/> Pixels: <b>1920x1080</b></div>
                  </div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
            </div>
            <div v-if="gameConfig.display_background_image" class="cdc-sub-content mt-2">
              <image-editor :show_slider="false" @saveImage="saveImage"></image-editor>
            </div>
            <div>
              <el-switch active-color="#e14eca" v-model="gameConfig.is_portrait_mode" @change="setLayoutCss()" />
              <label> &nbsp; Portrait mode</label>
            </div>
          </div>

        </div>
        <div v-show="activeTabIndex === 1">

          <!-- General -->
          <div class="cdc-section">
            <h4 class="cdc-section__title">General</h4>
            <div class="row">
              <div class="col">
                <base-input label="Background color">
                  <el-color-picker @input="setLayoutCss()" v-model="gameConfig.background_color" show-alpha></el-color-picker>
                </base-input>
              </div>
            </div>
          </div>

          <!-- Timer appearance -->
          <div class="cdc-section">
            <h4 class="cdc-section__title">Timer</h4>
            <div class="row">
              <div class="col-5">
                <base-input class="text-danger" label="Font">
                  <el-select @input="setTimerCss()"
                             v-model="gameConfig.font_family"
                             class="select-primary"
                             :style="{'font-family': gameConfig.font_family}"
                             name="font"
                             placeholder="Font"
                  >
                    <el-option
                      v-for="(f, i) in orderedFonts" :key="'B' + i"
                      :style="{'font-family': f.family}"
                      class="select-primary"
                      :label="f.family"
                      :value="f.family"
                    >
                    </el-option>
                  </el-select>
                </base-input>
              </div>
              <div class="col-3">
                <base-input @input="setTimerCss()" label="Font size" type="number" v-model="gameConfig.font_size">
                </base-input>
              </div>
              <div class="col-4">
                <base-input label="Text color">
                  <el-color-picker @input="setTimerCss()" v-model="gameConfig.text_color" show-alpha></el-color-picker>
                </base-input>
              </div>
            </div>
          </div>

          <!-- Interactions appearance -->
          <div class="cdc-section">
            <h4 class="cdc-section__title">Interactions</h4>
            <div class="row">
              <div class="col-5">
                <base-input class="text-danger" label="Font">
                  <el-select @input="setHintCss()"
                             v-model="gameConfig.hint_text_font"
                             class="select-primary"
                             :style="{'font-family': gameConfig.hint_text_font}"
                             name="font"
                             placeholder="Font"
                  >
                    <el-option
                      v-for="(f, i) in orderedFonts" :key="'B' + i"
                      :style="{'font-family': f.family}"
                      class="select-primary"
                      :label="f.family"
                      :value="f.family"
                    >
                    </el-option>
                  </el-select>
                </base-input>
              </div>
              <div class="col-3">
                <base-input @change="setHintCss()" label="Font size" type="number" v-model="gameConfig.hint_font_size">
                </base-input>
              </div>
              <div class="col-4">
                <base-input label="Text color">
                  <el-color-picker @input="setHintCss()" v-model="gameConfig.hint_text_color" show-alpha></el-color-picker>
                </base-input>
              </div>
            </div>
            <div class="row">
              <div v-for="(f, i) in fonts" :key="'C' + i">
                <link rel="stylesheet" :href="'https://fonts.googleapis.com/css?family=' + f.family">
              </div>
            </div>
          </div>

          <!-- Interaction icons -->
          <div class="cdc-section">
            <h4 class="cdc-section__title">Interaction icons</h4>
            <div class="row">
              <div class="col-4">
                <base-input label="Available interaction icon color">
                  <el-color-picker @input="setHintIconCss()" v-model="gameConfig.available_hint_color" show-alpha></el-color-picker>
                </base-input>
              </div>
              <div class="col-4">
                <base-input label="Used interaction icon color">
                  <el-color-picker @input="setHintIconCss()" v-model="gameConfig.used_hint_color" show-alpha></el-color-picker>
                </base-input>
              </div>
              <div class="col">
                <base-input @change="setHintIconCss()" label="Font size" type="number" v-model="gameConfig.hint_icon_font_size">
                </base-input>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6">
                <base-input label="Icon style">
                  <el-select
                    v-model="icon_style"
                    class="select-primary"
                    name="email_group"
                    placeholder="Select icon"
                    value-key="name"
                  >
                    <el-option
                      v-for="(icon, index) in icons"
                      class="select-primary"
                      :label="icon.description "
                      :value="{name: icon.name, description: icon.description}"
                      :key="'IC' + index"
                    >
                    <span style="float: left">
                      <i :class="'text-primary tim-icons ' + icon.name "></i> {{ icon.description }}</span>
                    </el-option>
                  </el-select>
                </base-input>
              </div>
            </div>
          </div>

        </div>

        <div class="row mt-3">
          <div class="col">
            <base-button type="primary" :disabled="loading" v-on:click="saveDisplayConfiguration()">{{ loading ? 'Saving...' : 'Save' }}</base-button>
          </div>
        </div>
      </card>
    </div>
    <div class="col-7">
      <card>
        <div slot="header" class="d-flex align-items-center justify-content-between">
          <h4 class="card-title mb-0">Layout</h4>
          <span class="cdc-replace-btn" @click="resetLayout()">
            <i class="tim-icons icon-refresh-01" style="font-size: 11px;"></i> Reset layout
          </span>
        </div>
        <p class="layout-help-text">Drag to reposition, resize from edges.</p>
        <div :key="j" :style="{'width': gameConfig.is_portrait_mode ? '50%' : '100%'}">
          <grid-layout :layout.sync="layout"
                       :style="layoutCss"
                       :col-num="3"
                       :max-rows="4"
                       :row-height="gameConfig.is_portrait_mode ? 150 : 100"
                       :is-draggable="true"
                       :is-resizable="true"
                       :vertical-compact="false"
                       :use-css-transforms="true"
          >
            <grid-item v-for="(item, index) in layout" :key="index + 'A'"
                       class="layout-grid-item"
                       :x="item.x"
                       :is-bounded="true"
                       :y="item.y"
                       :w="item.w"
                       :h="item.h"
                       :i="item.i"
            >
              <span class="layout-item-label">{{ item.name === 'Hints' ? 'Interactions' : item.name }}</span>
              <div v-if="item.name == 'Timer'" class="text">
                <p :style="timerCss" style="margin: auto">
                  {{ gameConfig.max_time }}</p>
              </div>
              <div v-if="item.name == 'Logo'" class="text">
                <img style="object-fit: contain" :src="item.content">
              </div>
              <div v-if="item.name == 'Hints'" class="text">
                <div style="float:right; margin-top: auto; margin-bottom: auto; overflow: hidden" v-for="j in parseInt(gameConfig.max_clues)" :key="j">
                  <i :style="hintIconCss" :class="'tim-icons ' + icon_style.name" class="ml-1"></i>
                </div>
              </div>
              <div v-if="item.name == 'Content'" class="text">
                <p :style="hintCss"
                   style="margin: auto">
                  [Interactions will be displayed here]</p>
              </div>
            </grid-item>
          </grid-layout>
        </div>
      </card>
    </div>
<!--    <div class="col-4">-->
<!--      -->

<!--    </div>-->
  </div>
</div>
</template>

<script>
import {Auth} from "aws-amplify";
import { ColorPicker, Select, Option, Switch } from 'element-ui';
import _ from "lodash";
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import VueGridLayout from 'vue-grid-layout';
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import icons from "@/assets/icons.json";
import ImageEditor from "@/components/ImageEditor";
import TabRail from "@/components/TabRail.vue";

export default {
  name: "DisplayConfiguration",
  components: {
    BreadcrumbItem,
    Breadcrumb,
    [ColorPicker.name]: ColorPicker,
    [Select.name]: Select,
    [Option.name]: Option,
    GridLayout: VueGridLayout.GridLayout,
    GridItem: VueGridLayout.GridItem,
    ImageEditor,
    TabRail,
    [Switch.name]: Switch
  },
  data() {
    return {
      layout: [
        {"x":0,"y":1,"w":3,"h":3,"i":"3", "name": "Content"},
      ],
      layoutCss: {},
      hintCss: {},
      timerCss: {},
      hintIconCss: {},
      loading: false,
      game: '',
      fonts: '',
      uploadAudioUrl: '',
      gameConfig: {
        id: '',
        img: '',
        background_color: '',
      },
      pre_hint_sound_file: '',
      background_music_sound_file: '',
      intro_video_file: '',
      activeTabIndex: 0,
      j: 0,
      icons: icons.icons,
      icon_style: '',
      i: 0,
      background_image: '',
      completion_media_file: '',
      introMediaType: 'video',
      completionMediaType: 'video'
    }
  },
  computed: {
    fromClues() {
      return this.$route.query.from === 'clues'
    },
    categories() {
      return [
        { name: 'General settings', shortName: 'General' },
        { name: 'Appearance', shortName: 'Appearance' }
      ];
    },
    orderedFonts: function () {
      _.remove(this.fonts, {
        family: 'Libre Baskerville'
      });
      _.remove(this.fonts, {
        family: 'Material Icons'
      });
      _.remove(this.fonts, {
        family: 'Material Icons Outlined'
      });
      _.remove(this.fonts, {
        family: 'Material Icons Sharp'
      });
      _.remove(this.fonts, {
        family: 'Material Icons Round'
      });
      _.remove(this.fonts, {
        family: 'M PLUS Rounded 1c'
      });
      return _.orderBy(this.fonts, 'family')
    }
  },
  methods: {
    detectMediaType(url) {
      if (!url) return 'video'
      const audioExtensions = ['.mp3', '.wav', '.ogg', '.aac', '.m4a', '.flac', '.wma']
      const lower = url.split('?')[0].toLowerCase()
      return audioExtensions.some(ext => lower.endsWith(ext)) ? 'audio' : 'video'
    },
    switchTab(index){
      this.activeTabIndex = index
      this.j++
    },
    setHintIconCss(){
      let size = parseInt(this.gameConfig.hint_icon_font_size) / 2
      this.hintIconCss = {
        'color': this.gameConfig.available_hint_color,
        'font-size': size + 'px'
      }
    },
    setTimerCss(){
      let size = parseInt(this.gameConfig.font_size) / 2
      this.timerCss = {
        'color': this.gameConfig.text_color,
        'font-family': this.gameConfig.font_family,
        'font-size': size + 'px'
      }
    },
    setHintCss(){
      let size = parseInt(this.gameConfig.hint_font_size) / 2
      this.hintCss = {
        'color': this.gameConfig.hint_text_color,
        'font-family': this.gameConfig.hint_text_font,
        'font-size': size + 'px'
      }
    },
    setLayoutCss(){
      if(this.gameConfig.display_background_image && this.background_image){
        // Same fit mapping as ClueDisplay.vue so the preview matches the live screen
        const fitMap = {
          'cover': 'cover',
          'fill': '100% 100%',
          'contain': 'contain',
          'scale-down': 'contain',
          'none': 'auto'
        }
        this.layoutCss = {'border-radius': '6px',
          'border': '1px solid rgba(255, 255, 255, 0.1)',
          'height': this.gameConfig.is_portrait_mode ? '650px' : '450px',
          'overflow': 'hidden',
          'background': 'url('+ this.background_image.url + ') '
            + this.background_image.background_color + ' no-repeat center',
          'background-size': fitMap[this.background_image.object_fit] || 'cover'}
      } else {
        this.layoutCss = {'background-color': this.gameConfig.background_color, 'border-radius': '6px',
          'border': '1px solid rgba(255, 255, 255, 0.1)',
          'overflow': 'hidden',
          'height': this.gameConfig.is_portrait_mode ? '650px' : '450px'}
      }
      this.j++
    },
    resetLayout(){
      let newLayout = [
        {"x":0,"y":1,"w":3,"h":3,"i":"3", "name": "Content"}
      ]
      if(this.gameConfig.has_timer){
        newLayout.push({"x":1,"y":0,"w":1,"h":1,"i":"0", "name": "Timer"})
      }
      if(this.gameConfig.display_logo){
        let logo = JSON.parse(localStorage.getItem('company')).img
        newLayout.push({"x":0,"y":0,"w":1,"h":1,"i":"1", "name": "Logo", "content": logo})
      }
      if(this.gameConfig.has_clue_limit){
        newLayout.push({"x":2,"y":0,"w":1,"h":1,"i":"2", "name": "Hints"})
      }
      this.layout = newLayout
    },
    toggleHintsLayout(){
      if(this.gameConfig.has_clue_limit){
        this.layout.push({"x":2,"y":0,"w":1,"h":1,"i":"2", "name": "Hints"})
      } else {
        this.layout = this.layout.filter(item => item.name !== 'Hints');
      }
    },
    toggleLogoLayout(){
      if(this.gameConfig.display_logo){
        let logo = JSON.parse(localStorage.getItem('company')).img
        this.layout.push({"x":0,"y":0,"w":1,"h":1,"i":"1", "name": "Logo", "content": logo})
      } else {
        this.layout = this.layout.filter(item => item.name !== 'Logo');
      }
    },
    toggleTimerLayout(){
      if(this.gameConfig.has_timer){
        this.layout.push({"x":1,"y":0,"w":1,"h":1,"i":"0", "name": "Timer"})
      } else {
        this.layout = this.layout.filter(item => item.name !== 'Timer');
      }
    },
    async saveAudioFile() {
      this.uploadAudioUrl = await this.getSignedURL(this.pre_hint_sound_file)
      let uploaded = await this.uploadFile(this.pre_hint_sound_file, this.uploadAudioUrl)
    },
    async saveBackgroundAudioFile(){
      this.uploadAudioUrl = await this.getSignedURL(this.background_music_sound_file)
      let uploaded = await this.uploadFile(this.background_music_sound_file, this.uploadAudioUrl)
    },
    handleIntroMediaUpload(event) {
      let file = event.target.files[0]
      // Clear the input so picking the same file again still fires change
      event.target.value = ''
      if (!file) return
      this.gameConfig.intro_video_url = URL.createObjectURL(file)
      this.gameConfig.intro_media_filename = file.name
      this.introMediaType = file.type.startsWith('audio/') ? 'audio' : 'video'
      this.intro_video_file = file
    },
    async saveIntroVideoFile() {
      this.uploadAudioUrl = await this.getSignedURL(this.intro_video_file)
      let uploaded = await this.uploadFile(this.intro_video_file, this.uploadAudioUrl)
    },
    async getSignedURL (file) {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'upload'

      let payload = {
        filePath: file.name,
        contentType: file.type
      }

      return new Promise ((resolve) => {
        axios.post(url, payload, config)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            this.$notify({ type: 'danger', message: 'File upload failed. Please try again.' })
            console.error(err)
          })
      })
    },
    uploadFile (file, url) {
      let config = { headers: {
          'Content-Type': file.type
        }}

      let payload = file
      return new Promise ( function(resolve) {
        axios.put(url, payload, config)
          .then((res) => {
            resolve(res)
          })
          .catch((err) => {
            console.error(err)
          })
      })
    },
    getFonts(){
      let url = API_LOCATION + 'fonts'

      axios.get(url)
        .then(response => {
          this.fonts = response.data
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response && err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.$notify({ type: 'danger', message: 'Something went wrong' })
          }
        })
    },
    async getClueDisplayConfigurations(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let gameId = this.game.id
      let url = API_LOCATION + 'companies/' + companyId + '/games/' + gameId + '/clue-display-configurations'
      axios.get(url, config)
        .then(response => {
          this.loading = false
          this.gameConfig = response.data.configurations
          // Cast DB integers to booleans for el-switch
          this.gameConfig.has_timer = Boolean(this.gameConfig.has_timer)
          this.gameConfig.has_clue_limit = Boolean(this.gameConfig.has_clue_limit)
          this.gameConfig.play_sound_before_hints = Boolean(this.gameConfig.play_sound_before_hints)
          this.gameConfig.play_sound_before_text = Boolean(this.gameConfig.play_sound_before_text)
          this.gameConfig.play_sound_before_custom = Boolean(this.gameConfig.play_sound_before_custom)
          this.gameConfig.play_sound_before_img = Boolean(this.gameConfig.play_sound_before_img)
          this.gameConfig.play_sound_before_audio = Boolean(this.gameConfig.play_sound_before_audio)
          this.gameConfig.play_sound_before_video = Boolean(this.gameConfig.play_sound_before_video)
          this.gameConfig.play_background_music = Boolean(this.gameConfig.play_background_music)
          this.gameConfig.loop_background_music = Boolean(this.gameConfig.loop_background_music)
          this.gameConfig.is_image_fullscreen = Boolean(this.gameConfig.is_image_fullscreen)
          this.gameConfig.is_video_fullscreen = Boolean(this.gameConfig.is_video_fullscreen)
          this.gameConfig.clear_video_hints = Boolean(this.gameConfig.clear_video_hints)
          this.gameConfig.display_logo = Boolean(this.gameConfig.display_logo)
          this.gameConfig.display_background_image = Boolean(this.gameConfig.display_background_image)
          this.gameConfig.is_portrait_mode = Boolean(this.gameConfig.is_portrait_mode)
          this.$set(this.gameConfig, 'play_intro_video', !!this.gameConfig.intro_video_url)
          if (!this.gameConfig.intro_video_timer_mode) {
            this.$set(this.gameConfig, 'intro_video_timer_mode', 'immediate')
          }
          this.introMediaType = this.detectMediaType(this.gameConfig.intro_media_filename || this.gameConfig.intro_video_url)
          this.$set(this.gameConfig, 'play_completion_media', !!this.gameConfig.completion_media_url)
          this.completionMediaType = this.detectMediaType(this.gameConfig.completion_media_filename || this.gameConfig.completion_media_url)
          this.background_image = response.data.background_image ? response.data.background_image[0] : null
          this.icon_style = { name: this.gameConfig.icon_style}
          if(response.data.layout.length > 0){
            this.layout = response.data.layout
            for(let n in this.layout){
              if(this.layout[n].name === 'Logo'){
                this.layout[n].content = JSON.parse(localStorage.getItem('company')).img
              }
            }
          } else {
            this.toggleLogoLayout()
            this.toggleTimerLayout()
            this.toggleHintsLayout()
          }
          this.setHintIconCss()
          this.setLayoutCss()
          this.setHintCss()
          this.setTimerCss()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response && err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.$notify({ type: 'danger', message: 'Something went wrong' })
          }
        })
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    validLayout(){
      for(let n in this.layout){
        if(this.layout[n].y > 4){
          return false
        }
        if((4-this.layout[n].y) < this.layout[n].h){
          return false
        }
      }
      return true
    },
    async saveImage(value){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/images'

      let data = {
        clue_display_configurations: this.gameConfig.id,
        url: value.url,
        scale: value.scale,
        description: value.description,
        background_color: value.background_color,
        x: value.x,
        y: value.y,
        is_primary: 0,
        object_fit: value.object_fit
      }

      axios.post(url, data, config)
        .then(response => {
          this.loading = false
          this.gameConfig.img = value.url
          this.getClueDisplayConfigurations()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response && err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.$notify({ type: 'danger', message: 'Something went wrong' })
          }
        })
    },
    async saveDisplayConfiguration(){
      this.loading = true
      if(!this.validLayout()){
        this.loading = false
        this.$notify({ type: 'danger', message: 'Layout components are out of bounds.' })
        return
      }
      if(this.pre_hint_sound_file != ''){
        await this.saveAudioFile()
        this.gameConfig.pre_hint_sound_file = this.uploadAudioUrl.split("?")[0]
      }

      if(this.background_music_sound_file != ''){
        await this.saveBackgroundAudioFile()
        this.gameConfig.background_music_sound_file = this.uploadAudioUrl.split("?")[0]
      }

      if(this.intro_video_file != ''){
        await this.saveIntroVideoFile()
        this.gameConfig.intro_video_url = this.uploadAudioUrl.split("?")[0]
      }

      if(this.completion_media_file != ''){
        await this.saveCompletionMediaFile()
        this.gameConfig.completion_media_url = this.uploadAudioUrl.split("?")[0]
      }

      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let gameId = this.game.id
      let url = API_LOCATION + 'companies/' + companyId + '/games/' + gameId + '/clue-display-configurations'
      let data = {
        img: this.gameConfig.img,
        background_color: this.gameConfig.background_color,
        text_color: this.gameConfig.text_color,
        font_family: this.gameConfig.font_family,
        has_timer: this.gameConfig.has_timer,
        max_clues: this.gameConfig.max_clues,
        max_time: this.gameConfig.max_time,
        has_clue_limit: this.gameConfig.has_clue_limit,
        display_background_image: this.gameConfig.display_background_image,
        display_logo: this.gameConfig.display_logo,
        hint_text_color: this.gameConfig.hint_text_color,
        hint_text_font: this.gameConfig.hint_text_font,
        is_video_fullscreen: this.gameConfig.is_video_fullscreen,
        clear_video_hints: this.gameConfig.clear_video_hints,
        is_image_fullscreen: this.gameConfig.is_image_fullscreen,
        available_hint_color: this.gameConfig.available_hint_color,
        used_hint_color: this.gameConfig.used_hint_color,
        font_size: this.gameConfig.font_size,
        hint_font_size: this.gameConfig.hint_font_size,
        play_sound_before_hints: this.gameConfig.play_sound_before_hints,
        play_sound_before_text: this.gameConfig.play_sound_before_text,
        play_sound_before_custom: this.gameConfig.play_sound_before_custom,
        play_sound_before_img: this.gameConfig.play_sound_before_img,
        play_sound_before_audio: this.gameConfig.play_sound_before_audio,
        play_sound_before_video: this.gameConfig.play_sound_before_video,
        pre_hint_sound_file: this.gameConfig.pre_hint_sound_file,
        hint_icon_font_size: this.gameConfig.hint_icon_font_size,
        auto_sync: this.gameConfig.auto_sync,
        id: this.gameConfig.id,
        layout: this.layout,
        icon_style: this.icon_style.name,
        play_background_music: this.gameConfig.play_background_music,
        loop_background_music: this.gameConfig.loop_background_music,
        background_music_sound_file: this.gameConfig.background_music_sound_file,
        is_portrait_mode: this.gameConfig.is_portrait_mode,
        intro_video_url: this.gameConfig.play_intro_video ? this.gameConfig.intro_video_url : null,
        intro_video_timer_mode: this.gameConfig.play_intro_video ? (this.gameConfig.intro_video_timer_mode || 'immediate') : null,
        intro_media_filename: this.gameConfig.play_intro_video ? (this.gameConfig.intro_media_filename || null) : null,
        completion_media_url: this.gameConfig.play_completion_media ? this.gameConfig.completion_media_url : null,
        completion_media_filename: this.gameConfig.play_completion_media ? (this.gameConfig.completion_media_filename || null) : null
      }
      axios.put(url, data, config)
        .then(response => {
          this.$notify({ type: 'success', message: 'Display configuration has been updated.' })
          this.getClueDisplayConfigurations()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response && err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.$notify({ type: 'danger', message: 'Something went wrong' })
          }
        })
    },
    handlePreviewSound(event){
      let file = event.target.files[0];
      event.target.value = ''
      if (!file) return
      this.gameConfig.pre_hint_sound_file = URL.createObjectURL(file);
      this.pre_hint_sound_file = file
      this.$emit('change', file);
    },
    handleBackgroundMusicFileUpload(event){
      let file = event.target.files[0];
      event.target.value = ''
      if (!file) return
      this.gameConfig.background_music_sound_file = URL.createObjectURL(file);
      this.background_music_sound_file = file
      this.i++
      this.$emit('change', file);
    },
    handleCompletionMediaUpload(event) {
      let file = event.target.files[0]
      event.target.value = ''
      if (!file) return
      this.gameConfig.completion_media_url = URL.createObjectURL(file)
      this.gameConfig.completion_media_filename = file.name
      this.completionMediaType = file.type.startsWith('audio/') ? 'audio' : 'video'
      this.completion_media_file = file
    },
    async saveCompletionMediaFile() {
      this.uploadAudioUrl = await this.getSignedURL(this.completion_media_file)
      let uploaded = await this.uploadFile(this.completion_media_file, this.uploadAudioUrl)
    },
  },
  mounted(){
    this.game = JSON.parse(localStorage.getItem("game"))
    this.getFonts()
    this.getClueDisplayConfigurations()
  }
}
</script>

<style scoped>
.cdc-section {
  border-left: 3px solid #e14eca;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 4px;
  padding: 16px 20px;
  margin-bottom: 16px;
}

.cdc-section__title {
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
}

.cdc-sub-content {
  padding-left: 24px;
}

.cdc-sub-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  margin-bottom: 4px;
}

.cdc-inline-checks {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
}

/* Dropzone */
.cdc-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 20px;
  border: 2px dashed rgba(225, 78, 202, 0.3);
  border-radius: 8px;
  background: rgba(30, 30, 47, 0.4);
  cursor: pointer;
  transition: all 0.15s ease;
}

.cdc-dropzone:hover {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.08);
}

.cdc-dropzone i {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.4);
  transition: color 0.15s ease;
}

.cdc-dropzone span {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  transition: color 0.15s ease;
}

.cdc-dropzone:hover i,
.cdc-dropzone:hover span {
  color: rgba(255, 255, 255, 0.8);
}

.cdc-dropzone--compact {
  flex-direction: row;
  gap: 8px;
  padding: 14px 20px;
}

/* File card (uploaded file) */
.cdc-file-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  background: rgba(30, 30, 47, 0.4);
}

.cdc-file-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: rgba(225, 78, 202, 0.1);
  flex-shrink: 0;
}

.cdc-file-card__preview {
  width: 60px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.3);
}

.cdc-video-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cdc-file-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cdc-file-card__name {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cdc-file-card__meta {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
}

.cdc-audio-player {
  height: 32px;
  width: 100%;
  max-width: 250px;
}

.cdc-file-card__actions {
  flex-shrink: 0;
}

.cdc-replace-btn {
  font-size: 12px;
  color: #e14eca;
  cursor: pointer;
  transition: color 0.15s ease;
}

.cdc-replace-btn:hover {
  color: #e76ee5;
}

.layout-help-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: -10px;
  margin-bottom: 12px;
}

.layout-grid-item {
  background: rgba(0, 0, 0, 0.2);
  border: 1px dashed rgba(225, 78, 202, 0.4);
  border-radius: 4px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.layout-grid-item:hover {
  border-color: #e14eca;
  box-shadow: 0 0 0 1px rgba(225, 78, 202, 0.2);
}

.layout-grid-item.vue-grid-item.vue-draggable-dragging {
  border-color: #e14eca;
  box-shadow: 0 4px 12px rgba(225, 78, 202, 0.3);
}

.layout-item-label {
  position: absolute;
  top: 4px;
  left: 6px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(225, 78, 202, 0.7);
  pointer-events: none;
  z-index: 1;
}

.vue-grid-item .resizing {
  opacity: 0.9;
}

.vue-grid-item .text {
  font-size: 24px;
  text-align: center;
  justify-content:center;
  display: flex;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  height: 100%;
  width: 100%;
}
</style>

<style>
.white-content .cdc-section {
  background: rgba(0, 0, 0, 0.02);
}

.white-content .cdc-sub-label {
  color: rgba(0, 0, 0, 0.55);
}

.white-content .cdc-dropzone {
  background: rgba(0, 0, 0, 0.02);
  border-color: rgba(225, 78, 202, 0.25);
}

.white-content .cdc-dropzone i {
  color: rgba(0, 0, 0, 0.3);
}

.white-content .cdc-dropzone span {
  color: rgba(0, 0, 0, 0.4);
}

.white-content .cdc-dropzone:hover {
  background: rgba(225, 78, 202, 0.05);
}

.white-content .cdc-dropzone:hover i,
.white-content .cdc-dropzone:hover span {
  color: rgba(0, 0, 0, 0.7);
}

.white-content .cdc-file-card {
  background: rgba(0, 0, 0, 0.02);
  border-color: rgba(0, 0, 0, 0.08);
}

.white-content .cdc-file-card__name {
  color: rgba(0, 0, 0, 0.8);
}

.white-content .cdc-file-card__meta {
  color: rgba(0, 0, 0, 0.4);
}

.white-content .layout-help-text {
  color: rgba(0, 0, 0, 0.4);
}

.white-content .layout-grid-item {
  background: rgba(0, 0, 0, 0.05);
  border-color: rgba(225, 78, 202, 0.3);
}
</style>
